import React, { useState, useEffect, useCallback } from "react";
import {
  ArrowLeft,
  Loader2,
  Search,
  Phone,
  Plus,
  Trash2,
  CheckCircle2,
  AlertCircle,
  Users,
  ChevronRight,
  Stethoscope,
  X,
  MapPin,
  ClipboardList,
  User,
  Barcode,
  ChevronDown,
} from "lucide-react";
import toast from "react-hot-toast";
import { AdminOrderManagementService } from "../../../../../services/api/admin-order.service";
import { OfflineLabtestService } from "../../../../../services/api/OfflineLabtest/offline-labtest.service";
import { CreateOfflineLabtestOrderPayload } from "../../../../../services/api/OfflineLabtest/types";
import { LabService } from "../../../../../services/api/LaborderService/lab.service";

interface CreateOfflineLabtestOrderScreenProps {
  clinicId: string;
  onBack: () => void;
  onSuccess: () => void;
}

interface BookedLabtestState {
  uniqueKey: string;
  labTestId: number;
  name: string;
  mrp: number;
  price: number;
  discount: number;
  assignedPatientId: string;
  assignedPatientName: string;
}

interface PatientOption {
  id: string;
  name: string;
  relation: string;
}

export const CreateOfflineLabtestOrderScreen: React.FC<CreateOfflineLabtestOrderScreenProps> = ({
  clinicId,
  onBack,
  onSuccess,
}) => {
  // Wizard Step: 1 = Patient Verification & Address/Doctor, 2 = Prepare Order & Select Tests & Payment
  const [step, setStep] = useState<1 | 2>(1);

  // ==================== STEP 1 STATES: PATIENT & ADDRESS ====================
  const [mobileNumber, setMobileNumber] = useState("");
  const [verifying, setVerifying] = useState(false);
  const [patient, setPatient] = useState<any | null>(null);

  // Register Form
  const [showRegisterForm, setShowRegisterForm] = useState(false);
  const [registerForm, setRegisterForm] = useState({ name: "", gender: "male", dateOfBirth: "" });
  const [registering, setRegistering] = useState(false);

  // Address & Doctor
  const [addresses, setAddresses] = useState<any[]>([]);
  const [selectedAddressText, setSelectedAddressText] = useState("");
  const [selectedPincode, setSelectedPincode] = useState("");
  const [customAddress, setCustomAddress] = useState("");
  const [customPincode, setCustomPincode] = useState("");
  const [useCustomAddress, setUseCustomAddress] = useState(false);
  const [doctorName, setDoctorName] = useState("");

  // ==================== STEP 2 STATES: TESTS & PAYMENT ====================
  const [familyOptions, setFamilyOptions] = useState<PatientOption[]>([]);

  // Selected Tests State
  const [bookedTests, setBookedTests] = useState<BookedLabtestState[]>([]);

  // Modal State for Catalogue Search
  const [showAddTestModal, setShowAddTestModal] = useState(false);
  const [catalogueSearchQuery, setCatalogueSearchQuery] = useState("");
  const [isHealthPackage, setIsHealthPackage] = useState(false);
  const [catalogueLoading, setCatalogueLoading] = useState(false);
  const [availableTests, setAvailableTests] = useState<any[]>([]);

  // Select Patient Modal State (For test row patient assignment)
  const [assigningTestKey, setAssigningTestKey] = useState<string | null>(null);
  const [showSelectPatientModal, setShowSelectPatientModal] = useState(false);

  // Add Family Member Modal State
  const [showAddFamilyModal, setShowAddFamilyModal] = useState(false);
  const [familyForm, setFamilyForm] = useState({ name: "", relation: "Spouse", gender: "male", age: "" });
  const [addingFamily, setAddingFamily] = useState(false);

  // Additional Discount & Remarks State
  const [showAdditionalDiscount, setShowAdditionalDiscount] = useState(false);
  const [additionalDiscount, setAdditionalDiscount] = useState<number>(0);
  const [remarks, setRemarks] = useState("");

  // Payment Type: "cash" | "online" | "split"
  const [paymentType, setPaymentType] = useState<"cash" | "online" | "split">("cash");
  const [splitCashAmount, setSplitCashAmount] = useState<number>(0);
  const [splitOnlineAmount, setSplitOnlineAmount] = useState<number>(0);

  const [submitting, setSubmitting] = useState(false);

  // ==================== STEP 1 HANDLERS ====================
  const handleVerifyMobile = async () => {
    if (!/^\d{10}$/.test(mobileNumber)) {
      toast.error("Please enter a valid 10-digit mobile number");
      return;
    }

    setVerifying(true);
    setPatient(null);
    setShowRegisterForm(false);

    try {
      const res = await AdminOrderManagementService.getPatientDetailsByMobile(mobileNumber);
      if (res.success && res.data) {
        setPatient(res.data);
        if (res.data.patientAddresses && res.data.patientAddresses.length > 0) {
          setAddresses(res.data.patientAddresses);
          const first = res.data.patientAddresses[0];
          setSelectedAddressText(first.address || first.streetAddress || "");
          setSelectedPincode(first.pincode || "");
        }
        toast.success(`Patient found: ${res.data.patientName}`);
      } else {
        toast.error("Patient not found. Please register patient.");
        setShowRegisterForm(true);
      }
    } catch (error) {
      console.error("Error verifying mobile number:", error);
      toast.error("Patient not found. Please register patient.");
      setShowRegisterForm(true);
    } finally {
      setVerifying(false);
    }
  };

  const handleRegisterPatient = async () => {
    if (!registerForm.name.trim() || !registerForm.dateOfBirth) {
      toast.error("Please fill patient name and date of birth");
      return;
    }

    try {
      setRegistering(true);
      const res = await AdminOrderManagementService.registerNewPatient({
        mobileNumber,
        name: registerForm.name.trim(),
        gender: registerForm.gender.toLowerCase(),
        dateOfBirth: registerForm.dateOfBirth,
      });

      if (res.success && res.data) {
        toast.success("Patient registered successfully!");
        setPatient({
          patientId: res.data.id || res.data.patientId,
          patientName: res.data.name || registerForm.name.trim(),
          patientMobileNumber: mobileNumber,
        });
        setShowRegisterForm(false);
      } else {
        toast.error(res.message || "Failed to register patient");
      }
    } catch (error: any) {
      console.error("Register patient error:", error);
      toast.error("Failed to register patient");
    } finally {
      setRegistering(false);
    }
  };

  const handleProceedToStep2 = async () => {
    if (!patient) {
      toast.error("Please select or register a patient first");
      return;
    }

    const finalAddress = useCustomAddress ? customAddress : selectedAddressText;
    const finalPincode = useCustomAddress ? customPincode : selectedPincode;

    if (!finalAddress.trim()) {
      toast.error("Please select or enter patient address");
      return;
    }
    if (!doctorName.trim()) {
      toast.error("Please enter Doctor Name");
      return;
    }

    // Fetch Patient & Family Options for test assignment
    try {
      const familyRes = await AdminOrderManagementService.getPatientAndFamilyDetails(patient.patientId);
      const options: PatientOption[] = [
        {
          id: patient.patientId,
          name: `${patient.patientName} (Self)`,
          relation: "Self",
        },
      ];

      if (familyRes.success && familyRes.data) {
        if (Array.isArray(familyRes.data.familyMemberDetails)) {
          familyRes.data.familyMemberDetails.forEach((mem: any) => {
            options.push({
              id: mem.familyMemberId || mem.id,
              name: `${mem.name} (${mem.relation || "Family"})`,
              relation: mem.relation || "Family",
            });
          });
        }
      }
      setFamilyOptions(options);
    } catch (err) {
      console.error("Failed to fetch family members:", err);
      setFamilyOptions([
        {
          id: patient.patientId,
          name: `${patient.patientName} (Self)`,
          relation: "Self",
        },
      ]);
    }

    setStep(2);
  };

  // ==================== CATALOGUE SEARCH API (MODAL) ====================
  const fetchAvailableLabtests = useCallback(async (query: string, pkg: boolean) => {
    try {
      setCatalogueLoading(true);
      const res = await LabService.getLabtestListing(1, 15, pkg, "", query);
      if (res.success && res.data) {
        setAvailableTests(res.data.labTestDetails || []);
      } else {
        setAvailableTests([]);
      }
    } catch (error) {
      console.error("Error fetching labtest catalogue:", error);
      setAvailableTests([]);
    } finally {
      setCatalogueLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!showAddTestModal) return;
    const timer = setTimeout(() => {
      fetchAvailableLabtests(catalogueSearchQuery, isHealthPackage);
    }, 400);
    return () => clearTimeout(timer);
  }, [catalogueSearchQuery, isHealthPackage, showAddTestModal, fetchAvailableLabtests]);

  const handleAddTestFromCatalogue = (test: any) => {
    const mrp = parseFloat(test.mrp || test.labtestPrice || test.price || test.offerPrice || 0);
    const price = parseFloat(test.price || test.offerPrice || test.finalPrice || mrp);
    const discount = Math.max(0, mrp - price);

    const newItem: BookedLabtestState = {
      uniqueKey: `${test.id}_${Date.now()}_${Math.random().toString(36).substring(2, 7)}`,
      labTestId: test.id,
      name: test.name || test.labtestName || "Lab Test",
      mrp,
      price,
      discount,
      assignedPatientId: familyOptions[0]?.id || patient?.patientId || "",
      assignedPatientName: familyOptions[0]?.name || patient?.patientName || "Self",
    };

    setBookedTests((prev) => [...prev, newItem]);
    toast.success(`Added "${newItem.name}"`);
  };

  const handleRemoveTest = (uniqueKey: string) => {
    setBookedTests((prev) => prev.filter((t) => t.uniqueKey !== uniqueKey));
  };

  const handleAssignPatientToTest = (uniqueKey: string, patientId: string) => {
    const selectedOpt = familyOptions.find((o) => o.id === patientId);
    setBookedTests((prev) =>
      prev.map((item) =>
        item.uniqueKey === uniqueKey
          ? {
            ...item,
            assignedPatientId: patientId,
            assignedPatientName: selectedOpt ? selectedOpt.name : "Assigned Patient",
          }
          : item
      )
    );
  };

  const handleAddFamilyMember = async () => {
    if (!familyForm.name.trim()) {
      toast.error("Please enter family member name");
      return;
    }

    try {
      setAddingFamily(true);
      const formData = new FormData();
      formData.append("patient_id", patient.patientId);
      formData.append("name", familyForm.name.trim());
      formData.append("relation", familyForm.relation);
      formData.append("gender", familyForm.gender);
      formData.append('status', 'Active');
      if (familyForm.age) formData.append("age", familyForm.age);

      const res = await AdminOrderManagementService.addFamilyMember(formData);
      if (res.success) {
        toast.success("Family member added!");
        setShowAddFamilyModal(false);
        setFamilyForm({ name: "", relation: "Spouse", gender: "male", age: "" });

        const familyRes = await AdminOrderManagementService.getPatientAndFamilyDetails(patient.patientId);
        const options: PatientOption[] = [
          { id: patient.patientId, name: `${patient.patientName} (Self)`, relation: "Self" },
        ];
        let newMemberId = "";
        let newMemberName = "";

        if (familyRes.success && familyRes.data?.familyMemberDetails) {
          familyRes.data.familyMemberDetails.forEach((mem: any) => {
            const memberId = mem.familyMemberId || mem.id;
            const name = `${mem.name} (${mem.relation || "Family"})`;
            options.push({ id: memberId, name, relation: mem.relation || "Family" });
            if (mem.name.toLowerCase() === familyForm.name.trim().toLowerCase()) {
              newMemberId = memberId;
              newMemberName = name;
            }
          });
        }
        setFamilyOptions(options);

        // Auto-assign newly added member if currently assigning for a test item
        if (assigningTestKey && newMemberId) {
          handleAssignPatientToTest(assigningTestKey, newMemberId);
          setShowSelectPatientModal(false);
          setAssigningTestKey(null);
        }
      } else {
        toast.error(res.message || "Failed to add family member");
      }
    } catch (err: any) {
      console.error("Add family member error:", err);
      toast.error("Failed to add family member");
    } finally {
      setAddingFamily(false);
    }
  };

  // ==================== FINANCIAL CALCULATIONS ====================
  const totalMRP = bookedTests.reduce((acc, t) => acc + (t.mrp || 0), 0);
  const totalItemDiscounts = bookedTests.reduce((acc, t) => acc + (t.discount || 0), 0);
  const grandTotalDiscount = totalItemDiscounts + additionalDiscount;
  const totalPayable = Math.max(0, totalMRP - grandTotalDiscount);

  // Sync Split Amounts when payment type or total payable changes
  useEffect(() => {
    if (paymentType === "cash") {
      setSplitCashAmount(totalPayable);
      setSplitOnlineAmount(0);
    } else if (paymentType === "online") {
      setSplitCashAmount(0);
      setSplitOnlineAmount(totalPayable);
    } else if (paymentType === "split") {
      if (splitCashAmount > totalPayable) {
        setSplitCashAmount(totalPayable);
        setSplitOnlineAmount(0);
      } else {
        setSplitOnlineAmount(Math.max(0, totalPayable - splitCashAmount));
      }
    }
  }, [paymentType, totalPayable]);

  // Submit Final Order
  const handleSubmitOrder = async () => {
    if (bookedTests.length === 0) {
      toast.error("Please add at least one lab test");
      return;
    }

    if (paymentType === "split" && Math.abs(splitCashAmount + splitOnlineAmount - totalPayable) > 0.01) {
      toast.error(`Cash amount (₹${splitCashAmount}) + Online amount (₹${splitOnlineAmount}) must equal Total Payable (₹${totalPayable})`);
      return;
    }

    const finalAddress = useCustomAddress ? customAddress : selectedAddressText;
    const finalPincode = useCustomAddress ? customPincode : selectedPincode;

    const payload: CreateOfflineLabtestOrderPayload = {
      clinicId: clinicId,
      patientId: patient.patientId,
      patientMobileNumber: mobileNumber,
      doctorName: doctorName.trim(),
      address: finalAddress.trim(),
      pincode: finalPincode.trim(),
      paymentType: paymentType,
      subtotal: totalMRP,
      discount: grandTotalDiscount,
      totalAmount: totalPayable,
      cashAmount: paymentType === "cash" ? totalPayable : paymentType === "online" ? 0 : splitCashAmount,
      onlineAmount: paymentType === "online" ? totalPayable : paymentType === "cash" ? 0 : splitOnlineAmount,
      remarks: remarks.trim(),
      labtests: bookedTests.map((item) => ({
        labTestId: item.labTestId,
        labTestName: item.name,
        patientId: item.assignedPatientId || patient.patientId,
        mrp: item.mrp,
        price: item.price,
        discount: item.discount,
      })),
    };

    try {
      setSubmitting(true);
      const res = await OfflineLabtestService.createOrder(payload);
      if (res.success) {
        toast.success(res.message || "Offline labtest order created successfully!");
        onSuccess();
      } else {
        toast.error(res.message || "Failed to create order");
      }
    } catch (error: any) {
      console.error("Create labtest order error:", error);
      toast.error(error?.message || "Failed to create offline labtest order");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="w-full flex flex-col h-full bg-white rounded-xl shadow-sm border border-gray-200/80 overflow-hidden">
      {/* Top Header Navigation */}
      <div className="px-6 py-4 border-b border-gray-200 bg-gray-50/80 flex items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <button
            onClick={step === 2 ? () => setStep(1) : onBack}
            className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-200/60 rounded-xl transition-colors flex items-center gap-1.5 text-xs font-semibold"
          >
            <ArrowLeft className="w-4 h-4" />
            <span>{step === 2 ? "Back" : "Back"}</span>
          </button>
          <div className="h-5 w-px bg-gray-300" />
          <div>
            <h2 className="text-base font-bold text-gray-900">Create Offline Labtest Order</h2>
            <p className="text-xs text-gray-500">
              Step {step} of 2 — {step === 1 ? "Patient Verification & Address" : "Prepare Order & Payment"}
            </p>
          </div>
        </div>
      </div>

      {/* STEP 1: PATIENT VERIFICATION, ADDRESS & DOCTOR INFO */}
      {step === 1 && (
        <div className="flex-1 overflow-y-auto p-6">
          <div className="max-w-xl mx-auto space-y-6 pt-2">
            <div className="text-center space-y-1">
              <div className="w-12 h-12 bg-emerald-100 text-emerald-700 rounded-full flex items-center justify-center mx-auto mb-2">
                <Phone className="w-6 h-6" />
              </div>
              <h3 className="text-base font-bold text-gray-900">Patient Mobile Verification</h3>
              <p className="text-xs text-gray-500">Enter patient's 10-digit mobile number to verify record or register new patient</p>
            </div>

            <div className="flex gap-2">
              <input
                type="text"
                maxLength={10}
                placeholder="Enter 10-digit Mobile Number"
                value={mobileNumber}
                onChange={(e) => setMobileNumber(e.target.value.replace(/\D/g, ""))}
                className="flex-1 px-4 py-2.5 border border-gray-300 rounded-xl text-sm focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
              />
              <button
                onClick={handleVerifyMobile}
                disabled={verifying || mobileNumber.length !== 10}
                className="px-6 py-2.5 bg-emerald-600 hover:bg-emerald-700 text-white font-semibold rounded-xl text-xs disabled:opacity-50 flex items-center gap-2"
              >
                {verifying ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
                <span>Verify</span>
              </button>
            </div>

            {/* Patient Found Box */}
            {patient && (
              <div className="p-4 bg-emerald-50 border border-emerald-200 rounded-xl flex items-center justify-between shadow-sm">
                <div className="flex items-center gap-3">
                  <CheckCircle2 className="w-6 h-6 text-emerald-600" />
                  <div>
                    <h5 className="font-bold text-gray-900 text-sm">{patient.patientName}</h5>
                    <p className="text-xs text-gray-600">Mobile: {patient.patientMobileNumber || mobileNumber}</p>
                  </div>
                </div>
              </div>
            )}

            {/* Register Patient Form if Not Found */}
            {showRegisterForm && !patient && (
              <div className="p-5 bg-gray-50 border border-gray-200 rounded-xl space-y-4 animate-in fade-in duration-200">
                <div className="flex items-center gap-2 text-amber-700 text-xs font-semibold bg-amber-50 p-2.5 rounded-lg border border-amber-200">
                  <AlertCircle className="w-4 h-4 shrink-0" />
                  <span>No record found for this mobile number. Register new patient below.</span>
                </div>

                <div className="space-y-3">
                  <div>
                    <label className="block text-xs font-semibold text-gray-700 mb-1">Patient Full Name *</label>
                    <input
                      type="text"
                      placeholder="e.g. John Doe"
                      value={registerForm.name}
                      onChange={(e) => setRegisterForm({ ...registerForm, name: e.target.value })}
                      className="w-full px-3.5 py-2 border border-gray-300 rounded-lg text-xs"
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs font-semibold text-gray-700 mb-1">Gender *</label>
                      <select
                        value={registerForm.gender}
                        onChange={(e) => setRegisterForm({ ...registerForm, gender: e.target.value })}
                        className="w-full px-3.5 py-2 border border-gray-300 rounded-lg text-xs"
                      >
                        <option value="male">Male</option>
                        <option value="female">Female</option>
                        <option value="other">Other</option>
                      </select>
                    </div>

                    <div>
                      <label className="block text-xs font-semibold text-gray-700 mb-1">Date of Birth *</label>
                      <input
                        type="date"
                        value={registerForm.dateOfBirth}
                        onChange={(e) => setRegisterForm({ ...registerForm, dateOfBirth: e.target.value })}
                        className="w-full px-3.5 py-2 border border-gray-300 rounded-lg text-xs"
                      />
                    </div>
                  </div>

                  <button
                    onClick={handleRegisterPatient}
                    disabled={registering}
                    className="w-full py-2.5 bg-emerald-600 hover:bg-emerald-700 text-white font-semibold rounded-lg text-xs flex items-center justify-center gap-2 shadow"
                  >
                    {registering && <Loader2 className="w-4 h-4 animate-spin" />}
                    <span>Register Patient & Select</span>
                  </button>
                </div>
              </div>
            )}

            {/* Address & Doctor Details Card */}
            {patient && (
              <div className="space-y-5 pt-4 border-t border-gray-200">
                {/* Address Selection */}
                <div className="space-y-3">
                  <label className="block text-xs font-bold uppercase tracking-wider text-gray-500">
                    Select Patient Address *
                  </label>

                  {addresses.length > 0 && !useCustomAddress && (
                    <div className="space-y-2">
                      {addresses.map((addr, idx) => (
                        <label
                          key={idx}
                          className={`flex items-start gap-3 p-3 rounded-xl border cursor-pointer transition-all ${selectedAddressText === (addr.address || addr.streetAddress)
                            ? "border-emerald-500 bg-emerald-50/50 text-emerald-900"
                            : "border-gray-200 bg-white hover:border-gray-300"
                            }`}
                        >
                          <input
                            type="radio"
                            name="address-choice"
                            checked={selectedAddressText === (addr.address || addr.streetAddress)}
                            onChange={() => {
                              setSelectedAddressText(addr.address || addr.streetAddress);
                              setSelectedPincode(addr.pincode || "");
                            }}
                            className="mt-1 text-emerald-600 focus:ring-emerald-500"
                          />
                          <div className="text-xs space-y-0.5">
                            <p className="font-semibold">{addr.address || addr.streetAddress}</p>
                            <p className="text-gray-500">Pincode: {addr.pincode}</p>
                          </div>
                        </label>
                      ))}
                    </div>
                  )}

                  <button
                    type="button"
                    onClick={() => setUseCustomAddress(!useCustomAddress)}
                    className="text-xs font-semibold text-emerald-600 hover:text-emerald-700 flex items-center gap-1"
                  >
                    <Plus className="w-3.5 h-3.5" />
                    <span>{useCustomAddress ? "Use Saved Patient Address" : "Enter Custom Address"}</span>
                  </button>

                  {useCustomAddress && (
                    <div className="p-4 bg-gray-50 border border-gray-200 rounded-xl space-y-3">
                      <div>
                        <label className="block text-xs font-semibold text-gray-700 mb-1">Street Address *</label>
                        <input
                          type="text"
                          placeholder="e.g. 45 Green Park, MG Road"
                          value={customAddress}
                          onChange={(e) => setCustomAddress(e.target.value)}
                          className="w-full px-3.5 py-2 border border-gray-300 rounded-lg text-xs"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-semibold text-gray-700 mb-1">Pincode *</label>
                        <input
                          type="text"
                          maxLength={6}
                          placeholder="560001"
                          value={customPincode}
                          onChange={(e) => setCustomPincode(e.target.value.replace(/\D/g, ""))}
                          className="w-full px-3.5 py-2 border border-gray-300 rounded-lg text-xs"
                        />
                      </div>
                    </div>
                  )}
                </div>

                {/* Doctor Details */}
                <div className="space-y-2">
                  <label className="block text-xs font-bold uppercase tracking-wider text-gray-500">Doctor Information *</label>
                  <div className="relative">
                    <Stethoscope className="w-4 h-4 text-gray-400 absolute left-3.5 top-2.5" />
                    <input
                      type="text"
                      placeholder="Doctor Name (e.g. Dr. Sarah Jenkins)"
                      value={doctorName}
                      onChange={(e) => setDoctorName(e.target.value)}
                      className="w-full pl-10 pr-3.5 py-2 border border-gray-300 rounded-xl text-xs focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                    />
                  </div>
                </div>

                <div className="pt-4 flex justify-end">
                  <button
                    onClick={handleProceedToStep2}
                    className="px-6 py-2.5 bg-emerald-600 hover:bg-emerald-700 text-white font-semibold rounded-xl text-xs flex items-center gap-2 shadow"
                  >
                    <span>Proceed to Select Labtests & Prepare Order</span>
                    <ChevronRight className="w-4 h-4" />
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* STEP 2: PREPARE ORDER, SELECT LABTESTS & SPLIT PAYMENT */}
      {step === 2 && (
        <div className="flex-1 overflow-y-auto p-6 flex flex-col lg:flex-row gap-6">
          {/* LEFT MAIN AREA (~70%): Order Details Banner & Tests Booked */}
          <div className="flex-1 space-y-6">
            {/* Top Patient Details & Doctor Cards Grid (Matching User Screenshot) */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* PATIENT DETAILS CARD */}
              <div className="p-4 bg-gray-50/50 rounded-2xl border border-gray-200/90 space-y-2.5">
                <h4 className="text-xs font-bold uppercase tracking-wider text-emerald-700 flex items-center gap-1.5">
                  <User className="w-4 h-4 text-emerald-600" /> PATIENT DETAILS
                </h4>
                <div className="text-sm font-bold text-gray-900">{patient?.patientName || "N/A"}</div>
                <div className="text-xs text-gray-600 flex items-center gap-1.5 font-medium">
                  <Phone className="w-3.5 h-3.5 text-gray-400" /> {patient?.patientMobileNumber || mobileNumber}
                </div>
                <div className="text-xs text-gray-600 flex items-start gap-1.5 pt-2 border-t border-gray-200/60 font-medium">
                  <MapPin className="w-3.5 h-3.5 text-gray-400 shrink-0 mt-0.5" />
                  <span>
                    {useCustomAddress ? customAddress : selectedAddressText}
                    {(useCustomAddress ? customPincode : selectedPincode) ? ` - ${useCustomAddress ? customPincode : selectedPincode}` : ""}
                  </span>
                </div>
              </div>

              {/* DOCTOR & SAMPLE INFO CARD */}
              <div className="p-4 bg-gray-50/50 rounded-2xl border border-gray-200/90 space-y-2.5">
                <h4 className="text-xs font-bold uppercase tracking-wider text-emerald-700 flex items-center gap-1.5">
                  <Stethoscope className="w-4 h-4 text-emerald-600" /> DOCTOR & SAMPLE INFO
                </h4>
                <div className="text-xs text-gray-600 flex items-center gap-1.5 pt-1">
                  <span className="text-gray-500">Attending Doctor:</span>
                  <span className="font-bold text-gray-900">{doctorName || "N/A"}</span>
                </div>
                <div className="text-xs text-gray-600 flex items-center gap-2 pt-1">
                  <Barcode className="w-4 h-4 text-gray-400" />
                  <span>Sample Barcode:</span>
                  <span className="font-mono bg-white px-2.5 py-0.5 rounded-md border border-gray-300 text-gray-800 font-bold text-[11px]">
                    Not Collected
                  </span>
                </div>
              </div>
            </div>

            {/* TESTS BOOKED SECTION */}
            <div className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="text-sm font-bold text-gray-900 uppercase tracking-wider">Tests Booked</h3>
                <button
                  type="button"
                  onClick={() => setShowAddTestModal(true)}
                  className="px-4 py-2 bg-emerald-50 hover:bg-emerald-100 text-emerald-700 border border-emerald-200 font-bold text-xs rounded-xl flex items-center gap-1.5 transition-all shadow-sm"
                >
                  <Plus className="w-4 h-4 text-emerald-600" />
                  <span>Add Test</span>
                </button>
              </div>

              {/* Tests Table or Empty State */}
              {bookedTests.length === 0 ? (
                <div className="py-14 flex flex-col items-center justify-center text-center border-2 border-dashed border-gray-200 rounded-2xl bg-gray-50/50 space-y-2">
                  <ClipboardList className="w-10 h-10 text-gray-300" />
                  <p className="text-xs font-bold text-gray-600">No labtests selected yet.</p>
                  <p className="text-[11px] text-gray-400">Click "+ Add Test" to add tests from catalogue.</p>
                </div>
              ) : (
                <div className="overflow-x-auto border border-gray-200 rounded-xl">
                  <table className="w-full text-left text-xs text-gray-600">
                    <thead className="bg-gray-50 border-b border-gray-200 font-bold text-gray-500 uppercase tracking-wider">
                      <tr>
                        <th className="px-4 py-3">Test / Package Name</th>
                        <th className="px-4 py-3 text-right">MRP (₹)</th>
                        <th className="px-4 py-3 text-right">Discount (₹)</th>
                        <th className="px-4 py-3 text-right">Price (₹)</th>
                        <th className="px-4 py-3">Ordered For</th>
                        <th className="px-4 py-3 text-center">Action</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {bookedTests.map((test) => (
                        <tr key={test.uniqueKey} className="hover:bg-gray-50/50">
                          <td className="px-4 py-3.5 font-bold text-gray-900">{test.name}</td>

                          {/* MRP Input */}
                          <td className="px-4 py-3.5 text-right">
                            <input
                              type="number"
                              min="0"
                              value={test.mrp === 0 ? "" : test.mrp}
                              placeholder="0"
                              onChange={(e) => {
                                const cleanVal = e.target.value.replace(/^0+(?=\d)/, "");
                                const val = cleanVal === "" ? 0 : parseFloat(cleanVal);
                                setBookedTests((prev) =>
                                  prev.map((t) =>
                                    t.uniqueKey === test.uniqueKey
                                      ? { ...t, mrp: val, price: Math.max(0, val - t.discount) }
                                      : t
                                  )
                                );
                              }}
                              className="w-24 px-2.5 py-1 border border-gray-300 rounded-lg text-right font-medium text-xs focus:ring-1 focus:ring-emerald-500"
                            />
                          </td>

                          {/* Discount Input */}
                          <td className="px-4 py-3.5 text-right">
                            <input
                              type="number"
                              min="0"
                              value={test.discount === 0 ? "" : test.discount}
                              placeholder="0"
                              onChange={(e) => {
                                const cleanVal = e.target.value.replace(/^0+(?=\d)/, "");
                                const val = cleanVal === "" ? 0 : parseFloat(cleanVal);
                                setBookedTests((prev) =>
                                  prev.map((t) =>
                                    t.uniqueKey === test.uniqueKey
                                      ? { ...t, discount: val, price: Math.max(0, t.mrp - val) }
                                      : t
                                  )
                                );
                              }}
                              className="w-24 px-2.5 py-1 border border-gray-300 rounded-lg text-right font-medium text-xs text-amber-700 focus:ring-1 focus:ring-emerald-500"
                            />
                          </td>

                          {/* Calculated Price */}
                          <td className="px-4 py-3.5 text-right font-bold text-emerald-700 text-sm">
                            ₹{test.price.toFixed(2)}
                          </td>

                          {/* Ordered For Button (Opens Select Patient Modal) */}
                          <td className="px-4 py-3.5">
                            <button
                              type="button"
                              onClick={() => {
                                setAssigningTestKey(test.uniqueKey);
                                setShowSelectPatientModal(true);
                              }}
                              className="px-3 py-1.5 bg-white hover:bg-gray-50 text-gray-800 border border-gray-300 rounded-xl font-semibold text-xs flex items-center justify-between gap-2 transition-all shadow-sm max-w-[180px] truncate"
                            >
                              <span className="truncate">{test.assignedPatientName || "Select Patient"}</span>
                              <ChevronDown className="w-3.5 h-3.5 text-gray-400 shrink-0" />
                            </button>
                          </td>

                          {/* Remove Action */}
                          <td className="px-4 py-3.5 text-center">
                            <button
                              type="button"
                              onClick={() => handleRemoveTest(test.uniqueKey)}
                              className="p-1.5 text-rose-500 hover:text-rose-700 hover:bg-rose-50 rounded-lg transition-colors"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>

          {/* RIGHT SIDEBAR (~30%): Financial Breakdown & Purple Payment Card */}
          <div className="w-full lg:w-80 shrink-0 space-y-5">
            <div className="bg-white rounded-2xl border border-gray-200 p-5 shadow-sm space-y-4">
              {/* Financial Lines */}
              <div className="space-y-2.5 text-xs text-gray-700 font-semibold">
                <div className="flex justify-between items-center">
                  <span>Total MRP</span>
                  <span className="text-sm font-bold text-gray-900">₹ {totalMRP.toFixed(2)}</span>
                </div>

                <div className="flex justify-between items-center text-emerald-600">
                  <span>Total Discount</span>
                  <span className="text-sm font-bold">₹ {grandTotalDiscount.toFixed(2)}</span>
                </div>

                <div className="flex justify-between items-center text-gray-400">
                  <span>Round Off</span>
                  <span className="font-bold">₹ 0</span>
                </div>

                {/* Additional Discount Input Toggle */}
                {!showAdditionalDiscount ? (
                  <button
                    type="button"
                    onClick={() => setShowAdditionalDiscount(true)}
                    className="text-xs font-bold text-emerald-600 hover:text-emerald-700 flex items-center gap-1 pt-1"
                  >
                    <Plus className="w-3.5 h-3.5" />
                    <span>Add additional Discount</span>
                  </button>
                ) : (
                  <div className="pt-2 border-t border-gray-100 space-y-1">
                    <label className="block text-[11px] font-bold text-gray-500">Additional Discount (₹)</label>
                    <input
                      type="number"
                      min="0"
                      value={additionalDiscount === 0 ? "" : additionalDiscount}
                      placeholder="0"
                      onChange={(e) => {
                        const cleanVal = e.target.value.replace(/^0+(?=\d)/, "");
                        setAdditionalDiscount(cleanVal === "" ? 0 : parseFloat(cleanVal));
                      }}
                      className="w-full px-3 py-1.5 border border-gray-300 rounded-lg text-xs text-amber-700 font-bold"
                    />
                  </div>
                )}
              </div>

              {/* PURPLE PAYMENT CARD (Matching user screenshot!) */}
              <div className="p-5 rounded-2xl bg-[#5D1D47] text-white space-y-4 shadow-md">
                <h4 className="text-sm font-bold tracking-wide">Payment Type</h4>

                {/* Radio Selection */}
                <div className="flex items-center justify-between text-xs font-bold">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="payment-mode-choice"
                      value="cash"
                      checked={paymentType === "cash"}
                      onChange={() => setPaymentType("cash")}
                      className="w-4 h-4 text-emerald-500 focus:ring-emerald-400 accent-emerald-500"
                    />
                    <span>Cash</span>
                  </label>

                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="payment-mode-choice"
                      value="online"
                      checked={paymentType === "online"}
                      onChange={() => setPaymentType("online")}
                      className="w-4 h-4 text-emerald-500 focus:ring-emerald-400 accent-emerald-500"
                    />
                    <span>Online</span>
                  </label>

                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="payment-mode-choice"
                      value="split"
                      checked={paymentType === "split"}
                      onChange={() => setPaymentType("split")}
                      className="w-4 h-4 text-emerald-500 focus:ring-emerald-400 accent-emerald-500"
                    />
                    <span>Split</span>
                  </label>
                </div>

                {/* Mode Details */}
                {paymentType === "cash" && (
                  <p className="text-xs font-semibold text-emerald-200">
                    Amount Paid in Cash: ₹{totalPayable.toFixed(2)}
                  </p>
                )}

                {paymentType === "online" && (
                  <p className="text-xs font-semibold text-emerald-200">
                    Amount Paid Online: ₹{totalPayable.toFixed(2)}
                  </p>
                )}

                {paymentType === "split" && (
                  <div className="space-y-3 pt-1">
                    <div>
                      <label className="block text-[10px] font-bold text-gray-200 mb-1">Paid via Cash (₹)</label>
                      <input
                        type="number"
                        min="0"
                        value={splitCashAmount === 0 ? "" : splitCashAmount}
                        placeholder="0"
                        onChange={(e) => {
                          const cleanVal = e.target.value.replace(/^0+(?=\d)/, "");
                          const cash = cleanVal === "" ? 0 : parseFloat(cleanVal);
                          setSplitCashAmount(cash);
                          setSplitOnlineAmount(Math.max(0, totalPayable - cash));
                        }}
                        className="w-full px-3 py-1.5 rounded-lg text-xs font-bold text-gray-900 bg-white"
                      />
                    </div>

                    <div>
                      <label className="block text-[10px] font-bold text-gray-200 mb-1">Paid Online (₹)</label>
                      <input
                        type="number"
                        min="0"
                        value={splitOnlineAmount === 0 ? "" : splitOnlineAmount}
                        placeholder="0"
                        onChange={(e) => {
                          const cleanVal = e.target.value.replace(/^0+(?=\d)/, "");
                          const online = cleanVal === "" ? 0 : parseFloat(cleanVal);
                          setSplitOnlineAmount(online);
                          setSplitCashAmount(Math.max(0, totalPayable - online));
                        }}
                        className="w-full px-3 py-1.5 rounded-lg text-xs font-bold text-gray-900 bg-white"
                      />
                    </div>
                  </div>
                )}
              </div>

              {/* Total Payable & Submit */}
              <div className="pt-2 flex items-center justify-between">
                <div>
                  <span className="block text-xs font-bold text-gray-500">Total Payable</span>
                  <span className="text-xl font-extrabold text-emerald-700">₹ {totalPayable.toFixed(2)}</span>
                </div>

                <button
                  type="button"
                  onClick={handleSubmitOrder}
                  disabled={submitting || bookedTests.length === 0}
                  className="px-6 py-2.5 bg-emerald-600 hover:bg-emerald-700 text-white font-bold rounded-xl text-xs flex items-center gap-2 shadow-lg disabled:opacity-50 transition-all"
                >
                  {submitting && <Loader2 className="w-4 h-4 animate-spin" />}
                  <span>Create Order</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ==================== SELECT PATIENT MODAL ==================== */}
      {showSelectPatientModal && (
        <div
          className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4"
          onClick={() => {
            setShowSelectPatientModal(false);
            setAssigningTestKey(null);
          }}
        >
          <div
            className="bg-white rounded-2xl w-full max-w-lg flex flex-col shadow-2xl overflow-hidden animate-in fade-in zoom-in-95 duration-200"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 bg-gray-50/50">
              <div>
                <h3 className="text-base font-bold text-gray-900">Select Patient for Lab Test</h3>
                <p className="text-xs text-gray-500">
                  {assigningTestKey
                    ? `Assigning patient for ${bookedTests.find((t) => t.uniqueKey === assigningTestKey)?.name || "selected test"}`
                    : "Select a family member or self"}
                </p>
              </div>
              <button
                onClick={() => {
                  setShowSelectPatientModal(false);
                  setAssigningTestKey(null);
                }}
                className="w-8 h-8 rounded-lg bg-gray-100 text-gray-500 flex items-center justify-center hover:bg-rose-50 hover:text-rose-500 transition-colors"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            {/* Modal Body: Patient & Family Members List + Add Member Button */}
            <div className="p-6 space-y-4 max-h-[70vh] overflow-y-auto">
              <div className="flex items-center justify-between">
                <span className="text-xs font-bold uppercase tracking-wider text-gray-500">Available Members</span>
                <button
                  type="button"
                  onClick={() => setShowAddFamilyModal(true)}
                  className="px-3.5 py-1.5 bg-emerald-50 hover:bg-emerald-100 text-emerald-700 border border-emerald-200 rounded-xl text-xs font-bold flex items-center gap-1.5 transition-colors"
                >
                  <Users className="w-3.5 h-3.5" />
                  <span>+ Add New Member</span>
                </button>
              </div>

              {/* List of Patients & Family Options */}
              <div className="space-y-2">
                {familyOptions.map((opt) => {
                  const currentAssigned = bookedTests.find((t) => t.uniqueKey === assigningTestKey)?.assignedPatientId;
                  const isSelected = currentAssigned === opt.id;

                  return (
                    <div
                      key={opt.id}
                      onClick={() => {
                        if (assigningTestKey) {
                          handleAssignPatientToTest(assigningTestKey, opt.id);
                        }
                        setShowSelectPatientModal(false);
                        setAssigningTestKey(null);
                      }}
                      className={`p-3.5 rounded-xl border cursor-pointer flex items-center justify-between transition-all ${isSelected
                        ? "border-emerald-500 bg-emerald-50/60 shadow-sm"
                        : "border-gray-200 hover:border-gray-300 hover:bg-gray-50/50"
                        }`}
                    >
                      <div className="flex items-center gap-3">
                        <div className={`w-9 h-9 rounded-full flex items-center justify-center font-bold text-xs ${isSelected ? "bg-emerald-600 text-white" : "bg-gray-100 text-gray-700"
                          }`}>
                          <User className="w-4 h-4" />
                        </div>
                        <div>
                          <p className="text-xs font-bold text-gray-900">{opt.name}</p>
                          <p className="text-[10px] text-gray-500">Relation: {opt.relation}</p>
                        </div>
                      </div>

                      {isSelected && <CheckCircle2 className="w-5 h-5 text-emerald-600" />}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ==================== CATALOGUE SEARCH MODAL (+ Add Test) ==================== */}
      {showAddTestModal && (
        <div
          className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4"
          onClick={() => setShowAddTestModal(false)}
        >
          <div
            className="bg-white rounded-2xl w-full max-w-4xl h-[80vh] flex flex-col shadow-2xl overflow-hidden animate-in fade-in zoom-in-95 duration-200"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 bg-white">
              <div>
                <h3 className="text-base font-bold text-gray-900">Add Lab Test</h3>
                <p className="text-xs text-gray-500">Select tests from catalogue</p>
              </div>
              <button
                onClick={() => setShowAddTestModal(false)}
                className="w-8 h-8 rounded-lg bg-gray-100 text-gray-500 flex items-center justify-center hover:bg-rose-50 hover:text-rose-500 transition-colors"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            {/* Modal Search & Filters */}
            <div className="px-6 py-3.5 border-b border-gray-100 flex flex-col sm:flex-row sm:items-center justify-between gap-3 bg-gray-50/50">
              <div className="relative flex-1">
                <Search className="w-4 h-4 absolute left-3 top-2.5 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search available tests by name..."
                  value={catalogueSearchQuery}
                  onChange={(e) => setCatalogueSearchQuery(e.target.value)}
                  className="w-full pl-9 pr-4 py-2 border border-gray-300 rounded-lg text-xs focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 bg-white font-semibold"
                />
              </div>

              <div className="flex items-center gap-2">
                <span className="text-xs font-bold text-gray-500">Package category:</span>
                <button
                  type="button"
                  onClick={() => setIsHealthPackage(!isHealthPackage)}
                  className={`px-3 py-1.5 rounded-full text-xs font-bold border transition-all cursor-pointer ${isHealthPackage
                    ? "bg-indigo-50 border-indigo-200 text-indigo-700"
                    : "bg-gray-100 border-gray-200 text-gray-600"
                    }`}
                >
                  {isHealthPackage ? "Health Packages" : "Individual Tests"}
                </button>
              </div>
            </div>

            {/* Catalogue Table */}
            <div className="flex-1 overflow-y-auto p-5">
              <div className="border border-gray-200 rounded-xl overflow-hidden h-full flex flex-col bg-white">
                <div className="grid grid-cols-[3fr_1.5fr_1.5fr_1fr] gap-2 text-[10px] font-bold text-gray-500 bg-gray-50 px-4 py-2.5 uppercase border-b border-gray-200">
                  <div>Test Name</div>
                  <div>Sample Type</div>
                  <div>Price (₹)</div>
                  <div className="text-center">Action</div>
                </div>

                {catalogueLoading ? (
                  <div className="flex items-center justify-center flex-1 py-12">
                    <Loader2 className="w-8 h-8 animate-spin text-emerald-600" />
                  </div>
                ) : availableTests.length === 0 ? (
                  <div className="flex items-center justify-center flex-1 py-12 text-gray-400 text-xs italic">
                    No labtests match found in catalogue.
                  </div>
                ) : (
                  <div className="flex-1 overflow-y-auto divide-y divide-gray-100">
                    {availableTests.map((t) => (
                      <div
                        key={t.id}
                        className="grid grid-cols-[3fr_1.5fr_1.5fr_1fr] gap-2 items-center px-4 py-3 hover:bg-gray-50/60 transition-colors"
                      >
                        <div>
                          <p className="text-xs font-bold text-gray-900">{t.name}</p>
                          {t.description && <p className="text-[10px] text-gray-400 truncate">{t.description}</p>}
                        </div>
                        <div className="text-xs text-gray-600 font-medium">{t.sampleType || "blood"}</div>
                        <div className="text-xs font-bold text-gray-900">₹{t.offerPrice || t.mrp || 0}</div>
                        <div className="text-center">
                          <button
                            type="button"
                            onClick={() => handleAddTestFromCatalogue(t)}
                            className="px-3 py-1 bg-emerald-50 text-emerald-700 border border-emerald-200 hover:bg-emerald-100 rounded-lg text-xs font-bold flex items-center gap-1 mx-auto transition-colors"
                          >
                            <Plus className="w-3.5 h-3.5" />
                            <span>Add</span>
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Add Family Member Modal */}
      {showAddFamilyModal && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 p-4">
          <div className="w-full max-w-sm bg-white rounded-xl shadow-xl p-5 space-y-4">
            <div className="flex justify-between items-center">
              <h4 className="text-sm font-bold text-gray-900">Add Family Member</h4>
              <button onClick={() => setShowAddFamilyModal(false)}>
                <X className="w-4 h-4 text-gray-400" />
              </button>
            </div>

            <div className="space-y-3 text-xs">
              <div>
                <label className="block font-semibold text-gray-700 mb-1">Name *</label>
                <input
                  type="text"
                  placeholder="Family Member Name"
                  value={familyForm.name}
                  onChange={(e) => setFamilyForm({ ...familyForm, name: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                />
              </div>

              <div>
                <label className="block font-semibold text-gray-700 mb-1">Relation *</label>
                <select
                  value={familyForm.relation}
                  onChange={(e) => setFamilyForm({ ...familyForm, relation: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                >
                  <option value="Spouse">Spouse</option>
                  <option value="Father">Father</option>
                  <option value="Mother">Mother</option>
                  <option value="Son">Son</option>
                  <option value="Daughter">Daughter</option>
                  <option value="Other">Other</option>
                </select>
              </div>

              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="block font-semibold text-gray-700 mb-1">Gender *</label>
                  <select
                    value={familyForm.gender}
                    onChange={(e) => setFamilyForm({ ...familyForm, gender: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  >
                    <option value="male">Male</option>
                    <option value="female">Female</option>
                  </select>
                </div>
                <div>
                  <label className="block font-semibold text-gray-700 mb-1">Age</label>
                  <input
                    type="number"
                    placeholder="e.g. 28"
                    value={familyForm.age}
                    onChange={(e) => setFamilyForm({ ...familyForm, age: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  />
                </div>
              </div>
            </div>

            <div className="flex justify-end gap-2 pt-2">
              <button
                onClick={() => setShowAddFamilyModal(false)}
                className="px-3 py-1.5 text-xs text-gray-600 hover:bg-gray-100 rounded-lg"
              >
                Cancel
              </button>
              <button
                onClick={handleAddFamilyMember}
                disabled={addingFamily}
                className="px-4 py-1.5 text-xs bg-emerald-600 hover:bg-emerald-700 text-white font-semibold rounded-lg flex items-center gap-1"
              >
                {addingFamily && <Loader2 className="w-3 h-3 animate-spin" />}
                <span>Save & Select</span>
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
